// src/pages/PontosColeta/PontosColetaPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { cowHead } from '@lucide/lab';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import { useBootstrap } from '../../context/BootstrapContext';
import { usePermissao } from '../../security/usePermissao';
import {
    atualizarPontoColeta,
    criarPontoColeta,
    desativarPontoColeta,
    listarPaginasColeta,
    listarPontosColeta,
    mensagemErro,
    reativarPontoColeta,
} from '../../infra/api/Coletas';
import { PAGINAS_COLETA, analisesDosProtocolos, rotuloAlvo, unidadeExibida } from '../../utils/coleta';
import { AZUL_CLARO } from '../../theme/cores';
import { MARGEM_LATERAL, TITULO_PAGINA } from '../../theme/escalaPagina';

const ORDEM_PAGINAS = ['LEITE', 'COLOSTRO', 'AGUA'];
const VAZIO = Object.freeze({ nome: '', ordem: '', analiseIds: [], local: '', descricao: '' });
const SITUACOES = [
    { valor: 'ATIVOS', rotulo: 'Pontos ativos' },
    { valor: 'DESATIVADOS', rotulo: 'Pontos desativados' },
    { valor: 'TODOS', rotulo: 'Todos os pontos' },
];

const IconeVaca = ({ size = 18 }) => <Lucide.Icon iconNode={cowHead} size={size} />;

/** Ícone da matriz: o do colostro é a vaca (como no Figma); os outros vêm do PAGINAS_COLETA. */
function IconeMatriz({ pagina, size = 18 }) {
    if (pagina === 'COLOSTRO') return <IconeVaca size={size} />;
    const Icone = Lucide[PAGINAS_COLETA[pagina]?.icone] || Lucide.MapPin;
    return <Icone size={size} />;
}

/**
 * Páginas (matrizes) em que o ponto aparece, na ordem leite → colostro → água. Ponto sem
 * análise marcada vale para todas as páginas configuradas.
 */
const matrizesDoPonto = (ponto, configs) => {
    const ids = ponto?.analiseIds || [];
    return (configs || [])
        .filter((c) => c.analiseId && (ids.length === 0 || ids.includes(c.analiseId)))
        .sort((a, b) => ORDEM_PAGINAS.indexOf(a.pagina) - ORDEM_PAGINAS.indexOf(b.pagina));
};

function PontoDialog({ open, ponto, analises, podeGerenciar, onClose, onSalvo, onDesativar, onReativar }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const inativo = ponto?.ativo === false;

    useEffect(() => {
        if (!open) return;
        setForm(
            ponto
                ? {
                      nome: ponto.nome || '',
                      ordem: ponto.ordem ?? '',
                      analiseIds: ponto.analiseIds || [],
                      local: ponto.local || '',
                      descricao: ponto.descricao || '',
                  }
                : VAZIO
        );
        setErro('');
    }, [open, ponto]);

    const ordem = String(form.ordem).trim() === '' ? null : Number(form.ordem);
    const ordemInvalida = ordem !== null && !Number.isInteger(ordem);
    const campo = (nome) => ({
        value: form[nome],
        onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.value })),
    });

    const salvar = async () => {
        setSalvando(true);
        setErro('');
        const corpo = {
            nome: form.nome.trim(),
            ordem,
            analiseIds: form.analiseIds,
            local: form.local.trim() || null,
            descricao: form.descricao.trim() || null,
        };
        try {
            onSalvo(ponto ? await atualizarPontoColeta(ponto.id, corpo) : await criarPontoColeta(corpo));
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível salvar o ponto de coleta.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm"
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>{ponto ? 'Editar ponto de coleta' : 'Cadastrar ponto de coleta'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    {inativo && <Alert severity="info">Este ponto está desativado e não aparece nas páginas.</Alert>}
                    <TextField autoFocus fullWidth label="Nome" placeholder="Ex.: Saída da ordenha" inputProps={{ maxLength: 80 }} {...campo('nome')} />
                    <TextField
                        select
                        fullWidth
                        label="Matriz (análises em que o ponto aparece)"
                        value={form.analiseIds}
                        onChange={(e) => setForm((f) => ({ ...f, analiseIds: e.target.value }))}
                        helperText={form.analiseIds.length === 0 ? 'Sem análise marcada, o ponto aparece em todas as páginas.' : ' '}
                        SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (ids) =>
                                ids.length === 0
                                    ? 'Todas as análises'
                                    : ids.map((id) => analises.find((a) => a.analiseId === id)?.analiseNome || id).join(', '),
                        }}
                        InputLabelProps={{ shrink: true }}
                    >
                        {analises.map((a) => (
                            <MenuItem key={a.analiseId} value={a.analiseId}>
                                <Checkbox size="small" checked={form.analiseIds.includes(a.analiseId)} />
                                <ListItemText primary={a.analiseNome} secondary={a.protocoloNome} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        label="Local"
                        placeholder="Ex.: Linha de ordenha / tanque 01"
                        helperText="Onde fica o ponto. Já vem como origem ao registrar uma análise aqui."
                        inputProps={{ maxLength: 80 }}
                        {...campo('local')}
                    />
                    <TextField
                        fullWidth
                        label="Ordem no processo"
                        inputProps={{ inputMode: 'numeric' }}
                        error={ordemInvalida}
                        helperText={ordemInvalida ? 'Use um número inteiro.' : 'Ordena o gráfico e as comparações: 1 = primeiro ponto do processo.'}
                        {...campo('ordem')}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Descrição (opcional)"
                        placeholder="Ex.: coletar com a torneira aberta por 1 minuto"
                        inputProps={{ maxLength: 280 }}
                        {...campo('descricao')}
                    />
                    {erro && <Alert severity="error">{erro}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
                {ponto && podeGerenciar && (
                    <Button
                        color={inativo ? 'primary' : 'error'}
                        disabled={salvando}
                        onClick={() => (inativo ? onReativar(ponto) : onDesativar(ponto))}
                        sx={{ mr: 'auto', fontWeight: 600 }}
                    >
                        {inativo ? 'Reativar ponto' : 'Desativar ponto'}
                    </Button>
                )}
                <Button onClick={onClose} disabled={salvando} color="inherit">Cancelar</Button>
                <Button onClick={salvar} variant="contained" disableElevation disabled={salvando || !form.nome.trim() || ordemInvalida}
                    sx={{ borderRadius: 2 }}>
                    {salvando ? 'Salvando…' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function PontoCard({ ponto, matrizes, bootstrap, podeGerenciar, podeRegistrar, onEditar, onNovaAnalise }) {
    const theme = useTheme();
    const inativo = ponto.ativo === false;
    // Ponto de todas as páginas não tem matriz própria: alfinete neutro.
    const principal = ponto.analiseIds?.length ? matrizes[0]?.pagina : null;
    const cor = PAGINAS_COLETA[principal]?.cor || theme.palette.text.secondary;
    const titulo = ponto.analiseIds?.length
        ? matrizes.map((m) => PAGINAS_COLETA[m.pagina]?.nomeCurto).filter(Boolean).join(' · ') || 'Análise sem página'
        : 'Todas as matrizes';
    const medidas = [
        ...new Set(
            matrizes.map((m) => {
                const meta = PAGINAS_COLETA[m.pagina];
                // Água é presença/ausência dos dois alvos do mesmo teste, não uma contagem.
                if (meta?.qualitativo) {
                    return `${(meta.alvosQualitativos || []).map((a) => a.rotulo).join(' e ')} · presença/ausência`;
                }
                const protocolo = (bootstrap?.protocolos || []).find((p) => p.id === m.protocoloId);
                return [rotuloAlvo(m.alvo), unidadeExibida(protocolo, m.unidade)].filter(Boolean).join(' · ');
            })
        ),
    ].filter(Boolean);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', opacity: inativo ? 0.7 : 1 }}>
            <Box sx={{ p: 2.5, flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ p: 0.9, borderRadius: 2, display: 'flex', color: cor, bgcolor: alpha(cor, 0.12) }}>
                        {principal ? <IconeMatriz pagina={principal} /> : <Lucide.MapPin size={18} />}
                    </Box>
                    <Chip
                        size="small"
                        label={inativo ? 'Desativado' : 'Ativo'}
                        sx={{
                            fontWeight: 600, borderRadius: 1.5,
                            color: inativo ? 'text.secondary' : 'success.dark',
                            bgcolor: inativo ? 'action.hover' : alpha(theme.palette.success.main, 0.12),
                        }}
                    />
                </Stack>
                <Typography variant="overline" color="text.secondary" display="block" sx={{ mt: 1.5, lineHeight: 1.6, letterSpacing: 1 }}>
                    {titulo}
                </Typography>
                <Typography fontWeight={700} sx={{ fontSize: '1.1rem', overflowWrap: 'anywhere' }}>{ponto.nome}</Typography>
                <Stack spacing={1} sx={{ mt: 1.5, color: 'text.secondary' }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Lucide.MapPin size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <Typography variant="body2">{ponto.local || 'Local não informado'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Lucide.FlaskConical size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <Typography variant="body2">{medidas.join(' | ') || 'Sem análise configurada'}</Typography>
                    </Stack>
                    {ponto.descricao && (
                        <Typography variant="caption" sx={{ pl: 3 }}>{ponto.descricao}</Typography>
                    )}
                </Stack>
            </Box>
            {(podeGerenciar || podeRegistrar) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}
                    sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    {podeGerenciar ? (
                        <Button startIcon={<Lucide.Pencil size={16} />} onClick={() => onEditar(ponto)}
                            sx={{ fontWeight: 700, color: AZUL_CLARO }}>
                            Editar ponto
                        </Button>
                    ) : <span />}
                    {podeRegistrar && !inativo && matrizes.length > 0 && (
                        <Button variant="outlined" color="inherit" startIcon={<Lucide.Plus size={16} />}
                            onClick={(e) => onNovaAnalise(ponto, matrizes, e.currentTarget)}
                            sx={{ borderRadius: 2, borderColor: 'divider', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Nova análise
                        </Button>
                    )}
                </Stack>
            )}
        </Paper>
    );
}

/**
 * Cadastro dos pontos de coleta da propriedade (layout do Figma): um cartão por ponto,
 * com a matriz (leite, colostro, água), o local e a análise; "Nova análise" abre o
 * registro na página da matriz com o ponto já escolhido. Desativar não apaga: amostras
 * antigas continuam mostrando o nome do ponto.
 */
export default function PontosColetaPage() {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const { bootstrap } = useBootstrap();
    const { pode } = usePermissao();
    const podeGerenciar = pode('pontoColeta.gerenciar');
    const podeRegistrar = pode('dados.alterar');
    const analises = useMemo(() => analisesDosProtocolos(bootstrap?.protocolos), [bootstrap?.protocolos]);

    const [pontos, setPontos] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [busca, setBusca] = useState('');
    const [matriz, setMatriz] = useState('');
    const [situacao, setSituacao] = useState('ATIVOS');
    const [editando, setEditando] = useState(undefined); // undefined = fechado, null = novo
    const [paraDesativar, setParaDesativar] = useState(null);
    const [menuNova, setMenuNova] = useState(null); // { ancora, ponto, matrizes }
    const [aviso, setAviso] = useState({ open: false, message: '', severity: 'info' });
    const avisar = (severity, message) => setAviso({ open: true, severity, message });

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const [lista, paginas] = await Promise.all([
                listarPontosColeta({ incluirInativos: true }),
                listarPaginasColeta().catch(() => []),
            ]);
            setPontos(lista || []);
            setConfigs(paginas || []);
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível carregar os pontos de coleta.'));
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const matrizesDisponiveis = useMemo(
        () => ORDEM_PAGINAS.filter((p) => configs.some((c) => c.pagina === p && c.analiseId)),
        [configs]
    );

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const posicao = (p) => {
            const m = matrizesDoPonto(p, configs);
            return p.analiseIds?.length ? ORDEM_PAGINAS.indexOf(m[0]?.pagina) : ORDEM_PAGINAS.length;
        };
        return pontos
            .filter((p) => situacao === 'TODOS' || (situacao === 'ATIVOS') === (p.ativo !== false))
            .filter((p) => !matriz || matrizesDoPonto(p, configs).some((m) => m.pagina === matriz))
            .filter((p) => !termo || [p.nome, p.local, p.descricao].some((t) => String(t || '').toLowerCase().includes(termo)))
            .sort((a, b) => posicao(a) - posicao(b) || (a.ordem ?? 9999) - (b.ordem ?? 9999) || a.nome.localeCompare(b.nome));
    }, [pontos, configs, busca, matriz, situacao]);

    const irParaNovaAnalise = (ponto, pagina) => {
        const rota = PAGINAS_COLETA[pagina]?.rota;
        if (rota) navigate(`/${tenant}/${rota}?registrar=1&ponto=${encodeURIComponent(ponto.id)}`);
    };

    const desativar = async () => {
        const ponto = paraDesativar;
        setParaDesativar(null);
        try {
            await desativarPontoColeta(ponto.id);
            setEditando(undefined);
            avisar('success', `${ponto.nome} desativado.`);
            carregar();
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível desativar o ponto.'));
        }
    };

    const reativar = async (ponto) => {
        try {
            await reativarPontoColeta(ponto.id);
            setEditando(undefined);
            avisar('success', `${ponto.nome} reativado.`);
            carregar();
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível reativar o ponto.'));
        }
    };

    return (
        <Box sx={{ px: MARGEM_LATERAL, py: { xs: 2, md: 3, xl: 4 }, maxWidth: 1400, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                        Controle microbiológico
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={TITULO_PAGINA}>Pontos de coleta</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Cadastre onde as amostras são coletadas e organize o histórico por local.
                    </Typography>
                </Box>
                {podeGerenciar && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<Lucide.Plus size={18} />}
                        onClick={() => setEditando(null)}
                        sx={{ borderRadius: 2, fontWeight: 700, flexShrink: 0, bgcolor: AZUL_CLARO, '&:hover': { bgcolor: alpha(AZUL_CLARO, 0.85) } }}
                    >
                        Cadastrar ponto
                    </Button>
                )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mt: 3, mb: 2.5 }}>
                <TextField
                    size="small"
                    placeholder="Buscar nome, local ou descrição"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    sx={{ width: { xs: '100%', md: 340 }, bgcolor: 'background.paper' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lucide.Search size={18} />
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{ 'aria-label': 'Buscar ponto de coleta' }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                        select
                        size="small"
                        value={matriz}
                        onChange={(e) => setMatriz(e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                        sx={{ flex: 1, minWidth: { md: 200 }, bgcolor: 'background.paper' }}
                        inputProps={{ 'aria-label': 'Matriz' }}
                    >
                        <MenuItem value="">Todas as matrizes</MenuItem>
                        {matrizesDisponiveis.map((p) => (
                            <MenuItem key={p} value={p}>{PAGINAS_COLETA[p].titulo}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        value={situacao}
                        onChange={(e) => setSituacao(e.target.value)}
                        sx={{ flex: 1, minWidth: { md: 200 }, bgcolor: 'background.paper' }}
                        inputProps={{ 'aria-label': 'Situação do ponto' }}
                    >
                        {SITUACOES.map((s) => (
                            <MenuItem key={s.valor} value={s.valor}>{s.rotulo}</MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Stack>

            {carregando ? (
                <Grid container spacing={2}>
                    {[0, 1, 2].map((i) => (
                        <Grid item xs={12} sm={6} lg={4} key={i}>
                            <Skeleton variant="rounded" height={230} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : visiveis.length === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 5, textAlign: 'center', color: 'text.secondary' }}>
                    <Lucide.MapPin size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {pontos.length === 0
                            ? `Nenhum ponto cadastrado.${podeGerenciar ? ' Cadastre o primeiro para usar nas análises.' : ''}`
                            : 'Nenhum ponto com estes filtros.'}
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {visiveis.map((p) => (
                        <Grid item xs={12} sm={6} lg={4} key={p.id}>
                            <PontoCard
                                ponto={p}
                                matrizes={matrizesDoPonto(p, configs)}
                                bootstrap={bootstrap}
                                podeGerenciar={podeGerenciar}
                                podeRegistrar={podeRegistrar}
                                onEditar={setEditando}
                                onNovaAnalise={(ponto, matrizes, ancora) =>
                                    matrizes.length === 1
                                        ? irParaNovaAnalise(ponto, matrizes[0].pagina)
                                        : setMenuNova({ ancora, ponto, matrizes })
                                }
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Ponto em mais de uma matriz: escolher em qual página registrar. */}
            <Menu anchorEl={menuNova?.ancora} open={Boolean(menuNova)} onClose={() => setMenuNova(null)}>
                {(menuNova?.matrizes || []).map((m) => (
                    <MenuItem
                        key={m.pagina}
                        onClick={() => {
                            const { ponto } = menuNova;
                            setMenuNova(null);
                            irParaNovaAnalise(ponto, m.pagina);
                        }}
                    >
                        <Box sx={{ display: 'flex', mr: 1.5, color: PAGINAS_COLETA[m.pagina]?.cor }}>
                            <IconeMatriz pagina={m.pagina} />
                        </Box>
                        {PAGINAS_COLETA[m.pagina]?.titulo}
                    </MenuItem>
                ))}
            </Menu>

            <PontoDialog
                open={editando !== undefined}
                ponto={editando}
                analises={analises}
                podeGerenciar={podeGerenciar}
                onClose={() => setEditando(undefined)}
                onSalvo={(salvo) => {
                    setEditando(undefined);
                    avisar('success', `${salvo.nome} salvo.`);
                    carregar();
                }}
                onDesativar={setParaDesativar}
                onReativar={reativar}
            />

            <ConfirmationDialog
                open={Boolean(paraDesativar)}
                onClose={() => setParaDesativar(null)}
                onConfirm={desativar}
                title="Desativar ponto de coleta"
                message={`Desativar "${paraDesativar?.nome || ''}"? Ele some das opções, mas as amostras já registradas continuam mostrando o nome.`}
                confirmText="Desativar"
                cancelText="Voltar"
            />

            <SnackbarAlert open={aviso.open} onClose={() => setAviso((a) => ({ ...a, open: false }))} severity={aviso.severity} message={aviso.message} />
        </Box>
    );
}
